import { useMemo } from 'react';
import { useProducts } from '../customHooks/useProducts';
import type { Product } from '../@types/product';
import { ProductType } from '../@types/product';
import type { Entitlement as EntitlementItem } from '../@types/entitlement';
import { EntitlementType } from '../@types/entitlement';
import { useGetEntitlements } from '../apiHooks/entitlements.hook';

/** ARAG insurance package entitlements — only present for full WohnenPlus subscribers */
const ARAG_PACKAGE_ENTITLEMENTS: EntitlementType[] = [
  EntitlementType.ARAG_WP_PACKAGE_S,
  EntitlementType.ARAG_WP_PACKAGE_M,
  EntitlementType.ARAG_WP_PACKAGE_L,
];

/**
 * Checks whether the user has any ARAG insurance package entitlement.
 * Used as the discriminator between full WohnenPlus and WohnenPlus Light.
 */
const hasAnyAragPackage = (entitlements: EntitlementItem[] | undefined): boolean =>
  entitlements?.some((e) => ARAG_PACKAGE_ENTITLEMENTS.includes(e.serviceType)) ?? false;

/**
 * Resolves whether a user has a given product type.
 *
 * The backend API (`/v2/active-products/my`) returns `WOHNEN_PLUS` for **both**
 * full WohnenPlus and WohnenPlus Light subscriptions. Since the product type alone
 * cannot distinguish between them, we use the presence of an ARAG insurance package
 * entitlement (`ARAG_WP_PACKAGE_S`, `ARAG_WP_PACKAGE_M`, or `ARAG_WP_PACKAGE_L`)
 * as a discriminator:
 *
 * - **WohnenPlus (full)**: has `WOHNEN_PLUS` product **AND** any ARAG package entitlement
 * - **WohnenPlusLight**: has `WOHNEN_PLUS` product **WITHOUT** any ARAG package entitlement
 *
 * WohnenPlus Light includes only: UTILITY_BILL_CHECK, STORAGE_SERVICE (Lagerraum),
 * Klarwatt, and MagentaTV (last two are not entitlements).
 *
 * All other product types (MieterPlus, KaeuferPlus) are resolved by direct match.
 */
const resolveHasProduct = (
  products: Product[] | undefined,
  productType: ProductType,
  hasArag: boolean,
  entitlementsLoaded: boolean,
): boolean => {
  if (!products?.length) return false;

  const has = (type: ProductType) => products.some((p) => p.productType === type);

  if (productType === ProductType.WohnenPlus || productType === ProductType.WohnenPlusLight) {
    if (!entitlementsLoaded) return false;
    const base = has(ProductType.WohnenPlus);
    return base && (productType === ProductType.WohnenPlus ? hasArag : !hasArag);
  }

  return has(productType);
};

/**
 * Internal hook that fetches products + entitlements once and resolves the product check.
 * All public hooks delegate to this to avoid duplicate `useGetEntitlements` calls.
 */
const useResolveProduct = (productType: ProductType) => {
  const { products, initializing: productsInitializing } = useProducts();
  const { entitlements, initializing: entitlementsInitializing } = useGetEntitlements();

  const hasArag = useMemo(() => hasAnyAragPackage(entitlements), [entitlements]);

  const hasProduct = useMemo(
    () => resolveHasProduct(products, productType, hasArag, !entitlementsInitializing),
    [productType, products, hasArag, entitlementsInitializing],
  );

  return { hasProduct, initializing: productsInitializing || entitlementsInitializing };
};

/**
 * Hook to check if the user has a specific product/subscription.
 *
 * @param productType - The product type to check for (e.g. `ProductType.WohnenPlus`, `ProductType.WohnenPlusLight`)
 * @returns `true` if the user has the specified product
 */
export const useGetHasProduct = (productType: ProductType): boolean => {
  return useResolveProduct(productType).hasProduct;
};

/** @returns `true` if the user has a MieterPlus subscription */
export const usePlusRentEntitlement = (): boolean => {
  return useGetHasProduct(ProductType.MieterPlus);
};

/** @returns `true` if the user has a full WohnenPlus subscription (not WohnenPlus Light) */
export const useLivingBundleEntitlement = (): boolean => {
  return useGetHasProduct(ProductType.WohnenPlus);
};

/** @returns `true` if the user has a WohnenPlus Light subscription */
export const useHasWohnenPlusLight = (): boolean => {
  return useGetHasProduct(ProductType.WohnenPlusLight);
};

/** @returns `true` if the user has any WohnenPlus subscription (full or Light) */
export const useHasAnyWohnenPlus = () => {
  const hasWohnenPlusFull = useGetHasProduct(ProductType.WohnenPlus);
  const hasWohnenPlusLight = useGetHasProduct(ProductType.WohnenPlusLight);
  return hasWohnenPlusFull || hasWohnenPlusLight;
};

/**
 * Same as {@link useGetHasProduct} but also exposes the loading state.
 *
 * @param productType - The product type to check for
 * @returns `{ hasProduct, isLoading }`
 */
export const useGetHasProductV2 = (productType: ProductType) => {
  const { hasProduct, initializing } = useResolveProduct(productType);
  return { hasProduct, isLoading: initializing };
};
